import { Box, Card, FormGroup, Stack, Typography } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1Button, C1LoadingButton } from 'c1ui'
import { C1Dialog, C1DialogActions, C1DialogContent, C1DialogTitle } from 'components/core/dialog/C1Dialog'
import { C1Link } from 'components/core/links/C1Link'
import { DocsLink } from 'components/core/links/docs'
import { C1LabeledTextFieldWithPresets } from 'components/core/RHKFormComponents/C1LabeledTextFieldWithPresets'
import { SearchUsers } from 'components/core/search/users'
import { useHasBuiltinRole } from 'components/no-ui/capability'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { getReassignPresets, getSelfReassignPresets } from 'components/pages/task/action/messages'
import { useAlert } from 'hooks/useAlert/index'
import { TaskValue } from 'lib/actions/task'
import { RoleID } from 'lib/constants/system'
import { UserContext } from 'lib/context/app'
import { routes } from 'lib/routes'
import { User, UserType } from 'pbts/c1/api/user/v1/user'
import React, { FC, useCallback, useContext, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useTicketActionTracker } from 'redux_toolkit/lib/TicketActionTracker'

type ReassignUserDialogV2Props = {
    task: TaskValue
} & ClosableProps

export const ReassignTaskDialogV2: FC<ReassignUserDialogV2Props> = ({ task, close }) => {
    const intl = useIntl()
    const toast = useAlert()
    const [comment, setComment] = useState('')
    const [users, setUsers] = useState<User[]>([])
    const [helperTextError, setHelperTextError] = useState<string | undefined>()
    const [userHelperTextError, setUserHelperTextError] = useState<string | undefined>()
    const ticketActionTracker = useTicketActionTracker()
    const isSuperAdmin = useHasBuiltinRole(RoleID.SuperAdmin)
    const userContext = useContext(UserContext)
    const currentUserId = userContext.user?.id

    const isReassigningToSelf = useMemo(() => {
        return users.length === 1 && users[0]?.id === currentUserId
    }, [users, currentUserId])

    const presets = useMemo(() => {
        return isReassigningToSelf ? getSelfReassignPresets(intl) : getReassignPresets(intl)
    }, [isReassigningToSelf, intl])

    const allowedReassignees = useMemo(() => {
        if (isSuperAdmin) {
            return []
        }

        const taskType = task.policy?.current?.instance.oneofKind
        if (taskType !== 'approval') {
            return []
        }
        const approval = task.policy?.current?.instance?.approval?.approval
        return approval?.allowReassignment ? approval.allowedReassignees : []
    }, [task.policy, isSuperAdmin])

    const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
        setComment(e.currentTarget.value)
    }, [])

    const handleSave = useCallback(async (): Promise<void> => {
        const instance = task.policy?.current?.instance
        const requireComment = instance &&
            instance.oneofKind === 'approval' &&
            instance.approval.approval?.requireReassignmentReason
        const cmt = comment.replaceAll(' ', '')
        if (requireComment && cmt.length === 0) {
            setHelperTextError(intl.formatMessage({
                id: 'reassign_action_reason_required',
                defaultMessage: 'A reason is required',
            }))
            return
        }
        if (users.length === 0) {
            setUserHelperTextError(intl.formatMessage({
                id: 'reassign_action_user_required',
                defaultMessage: 'A user is required',
            }))
            return
        }
        if (!isSuperAdmin && allowedReassignees.length > 0 && users.some((user) => !allowedReassignees.includes(user.id))) {
            setUserHelperTextError(intl.formatMessage({
                id: 'reassign_action_user_not_allowed',
                defaultMessage: 'User is not in the allowed reassignees list',
            }))
            return
        }

        try {
            await ticketActionTracker?.reassign(task, users, comment)
        } catch (err) {
            return toast(
                intl.formatMessage(
                    {
                        id: 'task_reassign_error',
                        defaultMessage: 'Error reassigning task. {code}: {msg}',
                    },
                    {
                        code: (err as RpcError).code,
                        msg: (err as RpcError).message,
                    },
                ),
                'error',
            )
        }

        toast(
            <FormattedMessage
                id="task_reassign_success"
                defaultMessage="Task {task_link} reassigned successfully"
                values={{
                    task_link: <C1Link href={routes.task(String(task.numericId || ''))}>{task.numericId}</C1Link>,
                }}
            />,
            'success',
        )
        close()
    }, [close, comment, intl, task, ticketActionTracker, toast, users, allowedReassignees, isSuperAdmin])

    return (
        <C1Dialog onClose={close} open fullWidth>
            <Card>
                <C1DialogTitle>
                    <FormattedMessage id="component_reassign_action_form" defaultMessage="Reassign task" />
                </C1DialogTitle>
                <C1DialogContent>
                    <Stack spacing={2}>
                        <Box mb={2}>
                            <Typography>
                                <FormattedMessage
                                    id="component_reassign_action_form_description"
                                    defaultMessage="Assign this task to a new user. <link>Learn more about completing tasks.</link>"
                                    values={{
                                        link: (chunks): React.ReactElement => <DocsLink path="/docs/product/how-to/access-change-tasks">{chunks}</DocsLink>,
                                    }}
                                />
                            </Typography>
                        </Box>
                        <FormGroup>
                            <SearchUsers
                                label={intl.formatMessage({
                                    id: 'component_reassign_action_user_pick_label',
                                    defaultMessage: 'Select assignee',
                                })}
                                autoFocus
                                onSelected={setUsers}
                                error={!!userHelperTextError}
                                helperText={userHelperTextError}
                                onFocus={(): void => {
                                    setUserHelperTextError(undefined)
                                }}
                                baseRequest={{
                                    ...(allowedReassignees.length > 0 ? { ids: allowedReassignees } : {}),
                                    excludeTypes: [UserType.AGENT],
                                }}
                            />
                        </FormGroup>
                        <C1LabeledTextFieldWithPresets
                            presets={presets}
                            value={comment}
                            setValue={setComment}
                            name="comment"
                            id="comment"
                            fullWidth
                            label={intl.formatMessage({
                                id: 'component_reassign_action_reason_label',
                                defaultMessage: 'Reason for reassignment',
                            })}
                            onChange={handleCommentChange}
                            onFocus={(): void => {
                                setHelperTextError(undefined)
                            }}
                            error={!!helperTextError}
                            helperText={helperTextError}
                        />
                    </Stack>
                </C1DialogContent>
                <C1DialogActions>
                    <C1Button onClick={close} variant="outlined">
                        <FormattedMessage id="component_task_action_form_comment_cancel" defaultMessage="Cancel" />
                    </C1Button>
                    <C1LoadingButton
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                        loading={!ticketActionTracker}
                    >
                        <FormattedMessage id="component_task_action_form_approve_submit" defaultMessage="Reassign" />
                    </C1LoadingButton>
                </C1DialogActions>
            </Card>
        </C1Dialog>
    )
}
